// Named-window (`WINDOW w AS (…)`) add/remove/rename/modify-body planner. Each
// entry point produces a flat PlannedByteEdit[] over the original source.
//
// Byte offsets are derived from the already-parsed `structure.windows`
// (NamedWindowDef[]) — `def.range` spans the whole `name AS (…)` definition and
// `def.spec.range` spans the `(…)` paren group (parens included). The WINDOW
// keyword position (needed for last-definition removal + no-clause synthesis)
// and inter-definition commas are located on demand from the keyword/comma
// scanners, mirroring how `applyStructuralEdit.cte.ts` re-locates the WITH
// keyword + commas the read-only structure doesn't cache.
//
// PRD / task: VQE Polish §4b-ii — named-window card real editing (ANA-01/02/03).

import type { NamedWindowDef, PlannedByteEdit, StructuralEdit, WindowSpec } from '../types';
import { foldIdentifier, readIdentifier, type ReadIdentResult } from '../scope/identifiers';
import {
  skipContextFor,
  trySkipLeadingTrivia,
  trySkipNonCode,
  type SkipContext,
} from '../classify/tokenSkip';
import { findTopLevelCommas } from '../structure/scanner';

import type { EditCtx } from './applyStructuralEdit';
import {
  deleteEdit,
  findClauseInsertionPoint,
  insertEdit,
  pickClauseInsertOffset,
  replaceEdit,
  scanStatementKeywords,
  trimTrailingWs,
} from './applyStructuralEdit.shared';

// Clause keywords that can only appear AFTER the WINDOW clause in standard SQL
// order — used both as the synthesis insertion boundary and (implicitly) to keep
// a synthesized WINDOW clause before ORDER BY / LIMIT / set-op / RETURNING.
const CLAUSE_BOUNDARIES_AFTER_WINDOW: readonly string[] = [
  'ORDER', 'LIMIT', 'OFFSET', 'FETCH',
  'UNION', 'INTERSECT', 'EXCEPT', 'MINUS', 'RETURNING', 'FOR',
];

export type NamedWindowEdit = Extract<
  StructuralEdit,
  {
    op:
      | 'rename-named-window'
      | 'remove-named-window'
      | 'add-named-window'
      | 'modify-named-window-body';
  }
>;

export function planNamedWindowEdit(
  ctx: EditCtx,
  edit: NamedWindowEdit,
): readonly PlannedByteEdit[] {
  const windows = ctx.structure.windows ?? [];
  switch (edit.op) {
    case 'rename-named-window':
      return planRenameNamedWindow(ctx, windows, edit);
    case 'remove-named-window':
      return planRemoveNamedWindow(ctx, windows, edit.index);
    case 'add-named-window':
      return planAddNamedWindow(ctx, windows, edit);
    case 'modify-named-window-body':
      return planModifyNamedWindowBody(ctx, windows, edit);
  }
}

// Replace the definition's name token (`WINDOW <name> AS (…)`) AND every
// `OVER <name>` reference to it, so a rename never orphans a live reference —
// mirrors `rename-cte`'s definition+reference coordination
// (`applyStructuralEdit.cte.ts:142-191`). The definition name is the first
// identifier inside `def.range`; its byte range is read on demand so a quoted
// name (`"w"`, `[w]`, `` `w` ``) is replaced whole.
//
// REFERENCE DISCOVERY IS FROM THE STATEMENT SOURCE, not from a pre-parsed field
// (§4b-ii-fix²). The resolver's `SelectItem.window.namedWindow` slot is UNSET
// for an un-aliased select item (`count(*) OVER w` — the select-list parser's
// implicit-alias heuristic swallows the bare `w` as the item's own alias before
// `extractWindowSpec` ever runs), so a field-only scan silently misses that
// (very common) shape and leaves `OVER w` dangling against a renamed `WINDOW
// win AS (…)`. We therefore additionally scan the source for every `OVER
// <target>` token — exactly as `rename-cte` discovers CTE refs from source
// (`findReferences`), not from a structural field. The trusted field-derived
// refs (already scoped to THIS statement's select list) are still emitted and
// DEDUPLICATED against the source scan so the aliased path is never regressed
// or double-edited; the source scan additionally covers un-aliased select
// items and any `OVER <target>` outside the select list (e.g. an ORDER BY
// window expr).
//
// Identity is conservative: a reference is only rewritten when its
// case/quote-folded name (`foldIdentifier`, the same helper used throughout the
// resolver for identifier comparison) matches the renamed definition's folded
// name — an unrelated `OVER other` is left untouched, and a quoted definition
// (case-sensitive) never coordinates a differently-cased bare reference.
//
// SCOPE + REFUSE (COMMON.md — emitting broken SQL is the one unacceptable
// outcome): only DEPTH-0 `OVER <target>` occurrences (this statement's own
// window scope) are rewritten. An occurrence buried inside a nested paren group
// is NOT rewritten — it may belong to an independent subquery scope (whose own
// `WINDOW` clause the rename must not touch) or to an extra wrapping paren we
// cannot attribute to this scope without a full parse. If ANY located `OVER
// <target>` occurrence is left uncovered, applying the definition rename would
// orphan it, so we REFUSE the whole rename (throw → `commitEdit` records a
// truthful commit-feedback failure and splices nothing) rather than emit a
// query-breaking partial.
//
// Window-INHERITANCE references (`w2 AS (w …)` — PG / MySQL 8.0 / SQLite
// base-window-name inheritance, §4b-ii-fix³) ARE additionally coordinated
// below, separately from the `OVER`-reference scan above: the FIRST non-trivia
// token inside ANY OTHER definition's spec paren can only be a base-window-name
// or a frame-clause leading keyword (`PARTITION`/`ORDER`/`ROWS`/`RANGE`/
// `GROUPS` — never a bare column), so reading it and fold-comparing to the
// rename target is unambiguous and false-positive-free — no refuse-net needed
// for this class.
function planRenameNamedWindow(
  ctx: EditCtx,
  windows: readonly NamedWindowDef[],
  edit: Extract<StructuralEdit, { op: 'rename-named-window' }>,
): readonly PlannedByteEdit[] {
  if (edit.index < 0 || edit.index >= windows.length) return [];
  const def = windows[edit.index];
  const name = readIdentifier(ctx.source, def.range.startOffset, def.spec.range.startOffset, ctx.options);
  if (!name) return [];
  const foldedTarget = foldIdentifier(name.value, name.quoted, ctx.options);

  // The definition name token — always edited.
  const edits: PlannedByteEdit[] = [
    replaceEdit(ctx, name.start, name.end, edit.newName, 'rename-named-window:definition'),
  ];

  // Every `OVER <target>` occurrence in the statement source, with its paren
  // depth (0 = this statement's own window scope).
  const occurrences = scanOverReferences(ctx, foldedTarget);

  // `covered` = byte ranges of the reference edits we can safely emit, keyed by
  // range so the two discovery sources never double-edit the same token:
  //   1. trusted field-derived refs (`SelectItem.window.namedWindow`) — already
  //      scoped to THIS statement's select list by the extractor;
  //   2. every DEPTH-0 source-scan occurrence.
  const covered = new Set<string>();

  for (const item of ctx.structure.selectItems ?? []) {
    const ref = readNamedWindowReference(ctx, item.window);
    if (!ref) continue;
    if (foldIdentifier(ref.value, ref.quoted, ctx.options) !== foldedTarget) continue;
    const key = rangeKey(ref.start, ref.end);
    if (covered.has(key)) continue;
    covered.add(key);
    edits.push(replaceEdit(ctx, ref.start, ref.end, edit.newName, 'rename-named-window:reference'));
  }

  for (const occ of occurrences) {
    if (occ.depth !== 0) continue;
    const key = rangeKey(occ.start, occ.end);
    if (covered.has(key)) continue;
    covered.add(key);
    edits.push(replaceEdit(ctx, occ.start, occ.end, edit.newName, 'rename-named-window:reference'));
  }

  // Window-INHERITANCE references (§4b-ii-fix³): loop over every OTHER
  // definition (skip the one being renamed) and read its spec's first inner
  // token. A chain (`w AS (…), w2 AS (w …), w3 AS (w2 …)`) falls out correctly
  // because each definition's OWN first token is checked independently against
  // `foldedTarget` — w3's first token is `w2`, not `w`, so renaming `w` never
  // touches w3.
  for (const [i, other] of windows.entries()) {
    if (i === edit.index) continue;
    const tok = readWindowBaseNameRef(ctx, other.spec);
    if (!tok) continue;
    if (foldIdentifier(tok.value, tok.quoted, ctx.options) !== foldedTarget) continue;
    const key = rangeKey(tok.start, tok.end);
    if (covered.has(key)) continue;
    covered.add(key);
    edits.push(replaceEdit(ctx, tok.start, tok.end, edit.newName, 'rename-named-window:reference'));
  }

  // Defense in depth: a located occurrence we could not cover would be orphaned
  // by the definition rename. Refuse rather than emit broken SQL.
  for (const occ of occurrences) {
    if (!covered.has(rangeKey(occ.start, occ.end))) {
      throw new Error(
        'Couldn’t locate every OVER reference to this window — a reference is nested inside a subquery '
        + 'or parentheses. Rename not applied; rename it in the SQL text instead.',
      );
    }
  }

  return edits;
}

function rangeKey(start: number, end: number): string {
  return `${start}:${end}`;
}

// A single ident-start char class matching the resolver's inline bare-ident
// guard (`/[A-Za-z_]/.test(source[i])`) used by `scanTopLevelKeywords`.
const BARE_IDENT_START = /[A-Za-z_]/;

interface OverReference {
  readonly start: number;
  readonly end: number;
  readonly depth: number;
}

// Scan the whole statement source for every `OVER <name>` reference whose folded
// name equals `foldedTarget`, recording each occurrence's paren depth. Mirrors
// the resolver's own top-level scanner (`scanTopLevelKeywords`): reads
// identifiers with the engine-aware `readIdentifier` (so a quoted window name
// `OVER "w"` / `` OVER `w` `` / `OVER [w]` is READ, not skipped), skips strings /
// comments / quoted regions via `trySkipNonCode`, and tracks paren depth. This
// is the source-derived discovery `rename-cte` uses in place of a pre-parsed
// field.
//
// EXCLUDED (never a coordinated reference): `OVER (` (an inline window spec —
// resumes after OVER so the `(` is depth-counted normally), the `WINDOW <name>
// AS` definition site (the name there follows WINDOW, not OVER, so it is never
// picked up), and any `<name>` inside a string / comment / quoted region
// (skipped by the tokenizer).
function scanOverReferences(ctx: EditCtx, foldedTarget: string): OverReference[] {
  const { source, options } = ctx;
  const start = ctx.statementInfo.range.startOffset;
  const end = ctx.statementInfo.range.endOffset;
  const skipCtx = skipContextFor(options);
  const out: OverReference[] = [];
  let depth = 0;
  let i = start;
  while (i < end) {
    const ident = readIdentifier(source, i, end, options);
    if (ident && (ident.quoted || BARE_IDENT_START.test(source[i]))) {
      if (!ident.quoted && ident.value.toUpperCase() === 'OVER') {
        const overDepth = depth;
        const j = skipTrivia(source, ident.end, end, skipCtx);
        if (j < end && source[j] === '(') {
          // `OVER (…)` inline spec — not a named-window reference.
          i = ident.end;
          continue;
        }
        const ref = readIdentifier(source, j, end, options);
        if (
          ref
          && (ref.quoted || BARE_IDENT_START.test(source[j]))
          && foldIdentifier(ref.value, ref.quoted, options) === foldedTarget
        ) {
          out.push({ start: ref.start, end: ref.end, depth: overDepth });
          i = ref.end;
          continue;
        }
      }
      i = ident.end;
      continue;
    }
    const skipped = trySkipNonCode(source, i, end, skipCtx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { depth++; i++; continue; }
    if (c === ')') { if (depth > 0) depth--; i++; continue; }
    i++;
  }
  return out;
}

// Skip whitespace + comments ONLY (never strings / quoted identifiers) between
// the OVER keyword and its window-name token, so a quoted window name
// (`OVER "w"`) is left for `readIdentifier` to read rather than skipped as a
// quoted region.
function skipTrivia(source: string, from: number, end: number, skipCtx: SkipContext): number {
  let i = from;
  while (i < end) {
    const c = source[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { i++; continue; }
    const t = trySkipLeadingTrivia(source, i, end, skipCtx);
    if (t > i) { i = t; continue; }
    break;
  }
  return i;
}

// Locate the byte range of a select item's `OVER <name>` reference token, when
// `window.namedWindow` is set (an inline `OVER (…)` spec leaves `namedWindow`
// unset and is skipped). NOT a third parser: `extractWindowSpec`
// (`extractQueryStructure.window.ts`) records a named-window reference's
// `WindowSpec.range` as `[OVER-keyword-start, identifier-end)`, so the name
// token's end is already `range.endOffset` and its start is deterministically
// `range.startOffset + 'OVER'.length` plus whitespace — this re-derives the
// SAME identifier through the SAME `readIdentifier` primitive the extractor
// itself used, from offsets already on `QueryStructure`.
function readNamedWindowReference(
  ctx: EditCtx,
  window: WindowSpec | undefined,
): ReadIdentResult | null {
  if (!window?.namedWindow) return null;
  const { source } = ctx;
  const overEnd = window.range.startOffset + 'OVER'.length;
  if (source.slice(window.range.startOffset, overEnd).toUpperCase() !== 'OVER') return null;
  let i = overEnd;
  while (i < window.range.endOffset && /\s/.test(source[i])) i++;
  const ident = readIdentifier(source, i, window.range.endOffset, ctx.options);
  return ident && ident.end === window.range.endOffset ? ident : null;
}

// The frame-clause leading keywords — when a window-spec paren's first inner
// token is one of these, the spec has NO base window (it starts straight into
// PARTITION BY / ORDER BY / a frame clause). Any other identifier in that
// position can only be a base-window-name (window-spec grammar has no
// expression / column position before PARTITION/ORDER/frame), so the absence
// of a match here is the sole signal we need — no further disambiguation.
const WINDOW_FRAME_LEADING_KEYWORDS = new Set(['PARTITION', 'ORDER', 'ROWS', 'RANGE', 'GROUPS']);

// Read a window definition's base-window-name reference (§4b-ii-fix³): the
// first non-trivia token inside its spec paren, when that token is a plausible
// base-window-name rather than a frame-clause leading keyword or an empty
// body. Mirrors `planModifyNamedWindowBody`'s inner-paren offsets (`spec.range
// .startOffset + 1` = just inside the open paren; `endOffset - 1` = the close
// paren) and reuses the same `skipTrivia` + `readIdentifier` primitives
// `scanOverReferences` uses for `OVER <name>` — quoting/fold identity is
// therefore respected identically.
function readWindowBaseNameRef(ctx: EditCtx, spec: WindowSpec): ReadIdentResult | null {
  const { source, options } = ctx;
  const innerStart = spec.range.startOffset + 1;
  let innerEnd = spec.range.endOffset;
  if (innerEnd > innerStart && source[innerEnd - 1] === ')') innerEnd -= 1;
  const skipCtx = skipContextFor(options);
  const j = skipTrivia(source, innerStart, innerEnd, skipCtx);
  if (j >= innerEnd) return null;
  const tok = readIdentifier(source, j, innerEnd, options);
  if (!tok || (!tok.quoted && !BARE_IDENT_START.test(source[j]))) return null;
  if (!tok.quoted && WINDOW_FRAME_LEADING_KEYWORDS.has(tok.value.toUpperCase())) return null;
  return tok;
}

// Replace the inner body (between the definition's parens) with `newBodyText`.
function planModifyNamedWindowBody(
  ctx: EditCtx,
  windows: readonly NamedWindowDef[],
  edit: Extract<StructuralEdit, { op: 'modify-named-window-body' }>,
): readonly PlannedByteEdit[] {
  if (edit.index < 0 || edit.index >= windows.length) return [];
  const def = windows[edit.index];
  const specRange = def.spec.range;
  const innerStart = specRange.startOffset + 1; // just inside the open paren
  // `spec.range` ends one-past the matching close paren; a malformed / unclosed
  // body has no close paren, so only trim the trailing `)` when it's really there.
  let innerEnd = specRange.endOffset;
  if (innerEnd > innerStart && ctx.source[innerEnd - 1] === ')') innerEnd -= 1;
  return [replaceEdit(ctx, innerStart, innerEnd, edit.newBodyText, 'modify-named-window-body')];
}

// Remove a single definition, fixing comma separators. Removing the sole
// remaining definition drops the whole `WINDOW …` clause (keyword included).
function planRemoveNamedWindow(
  ctx: EditCtx,
  windows: readonly NamedWindowDef[],
  index: number,
): readonly PlannedByteEdit[] {
  if (index < 0 || index >= windows.length) return [];
  if (windows.length === 1) {
    const kwStart = locateWindowKeyword(ctx, windows[0].range.startOffset);
    const start = kwStart >= 0
      ? trimTrailingWs(ctx.source, ctx.statementInfo.range.startOffset, kwStart)
      : windows[0].range.startOffset;
    return [deleteEdit(ctx, start, windows[0].range.endOffset, 'remove-named-window:drop-clause')];
  }
  if (index === 0) {
    // Drop the first definition plus its trailing comma + the leading whitespace
    // up to the next definition's start (mirrors remove-cte:first).
    return [deleteEdit(ctx, windows[0].range.startOffset, windows[1].range.startOffset, 'remove-named-window:first')];
  }
  // Drop the preceding comma + this definition.
  const prevComma = commaBetween(ctx, windows[index - 1], windows[index]);
  const start = prevComma >= 0 ? prevComma : windows[index].range.startOffset;
  return [deleteEdit(ctx, start, windows[index].range.endOffset, 'remove-named-window:middle-or-last')];
}

function planAddNamedWindow(
  ctx: EditCtx,
  windows: readonly NamedWindowDef[],
  edit: Extract<StructuralEdit, { op: 'add-named-window' }>,
): readonly PlannedByteEdit[] {
  const defText = `${edit.name} AS (${edit.bodyText})`;
  if (windows.length === 0) {
    const anchor = windowSynthAnchor(ctx);
    const kws = scanStatementKeywords(ctx);
    const boundary = findClauseInsertionPoint(
      kws, anchor, CLAUSE_BOUNDARIES_AFTER_WINDOW, ctx.statementInfo.range.endOffset,
    );
    const at = pickClauseInsertOffset(ctx.source, anchor, boundary, ctx.statementInfo.range.startOffset);
    return [insertEdit(ctx, at, ` WINDOW ${defText}`, 'add-named-window:synth')];
  }
  const last = windows.length - 1;
  const afterIndex = edit.afterIndex;
  if (afterIndex === undefined || afterIndex >= last) {
    return [insertEdit(ctx, windows[last].range.endOffset, `, ${defText}`, 'add-named-window:append')];
  }
  if (afterIndex < 0) {
    return [insertEdit(ctx, windows[0].range.startOffset, `${defText}, `, 'add-named-window:prepend')];
  }
  return [insertEdit(ctx, windows[afterIndex].range.endOffset, `, ${defText}`, 'add-named-window:middle')];
}

// End-of-clause anchor for a synthesized WINDOW clause: the last byte of the
// clause that immediately precedes WINDOW in standard SQL order (HAVING → GROUP
// BY → WHERE → JOINS → FROM → SELECT). The caller wraps the result with a
// leading space so the inserted clause separates from its predecessor.
function windowSynthAnchor(ctx: EditCtx): number {
  const s = ctx.structure;
  if (s.having?.length) return s.having[s.having.length - 1].range.endOffset;
  if (s.groupBy?.length) return s.groupBy[s.groupBy.length - 1].range.endOffset;
  if (s.where?.length) return s.where[s.where.length - 1].range.endOffset;
  if (s.joins?.length) return s.joins[s.joins.length - 1].range.endOffset;
  if (s.fromItems?.length) return s.fromItems[s.fromItems.length - 1].range.endOffset;
  if (s.selectItems?.length) return s.selectItems[s.selectItems.length - 1].range.endOffset;
  return ctx.statementInfo.range.endOffset;
}

// Locate the WINDOW keyword that introduces the clause containing the first
// definition — the last top-level `WINDOW` keyword starting before `firstDefStart`.
function locateWindowKeyword(ctx: EditCtx, firstDefStart: number): number {
  const kws = scanStatementKeywords(ctx);
  let best = -1;
  for (const k of kws) {
    if (k.upper === 'WINDOW' && k.start < firstDefStart) best = k.start;
  }
  return best;
}

// First top-level comma between two adjacent definitions (the separator).
function commaBetween(ctx: EditCtx, prev: NamedWindowDef, next: NamedWindowDef): number {
  const commas = findTopLevelCommas(ctx.source, prev.range.endOffset, next.range.startOffset, ctx.options);
  return commas.length > 0 ? commas[0] : -1;
}
